import { EVENT_SYSTEM, plural } from './vocab.js';
import { SNIPPET_CARD, TASKS_CARD } from './knowledgeChat.js';

const KIND_HINTS = [
    ['MONETIZATION', /\b(price|prices|pricing|revenue|bundle|bundles|offer|offers|iap|purchase|spend|gems?|coins?|discount|\$)/],
    ['PROGRESSION', /\b(xp|level|levels|tier|tiers|unlock|progress|reward|rewards|milestone|battle pass|season pass)\b/],
    ['AUDIENCE', /\b(players?|audience|segment|segments|whales|new users|region|country|india|payers?)\b/],
    ['LIVEOPS', /\b(event|events|weekly|daily|monthly|reset|cadence|refresh|rotation|calendar|tournament)\b/],
    ['TECHNICAL', /\b(server|client|build|platform|sdk|cache|crash|cap|capped|limit)\b/],
    ['GAMEPLAY', /\b(mode|modes|match|matches|combat|loop|gameplay|mechanic)\b/],
    ['CONTENT', /\b(art|content|banner|asset|assets|copy|release)\b/],
    ['ACQUISITION', /\b(ua|install|installs|roas|campaign|acquisition)\b/],
];

const guessKind = (text) => KIND_HINTS.find(([, re]) => re.test(text.toLowerCase()))?.[0] || 'GENERAL';

const STEP_VERB = /^(set|add|make|refresh|update|create|raise|lower|double|halve|run|check|publish|send|prepare|design|price|pick|schedule|remove|swap|enable|disable|increase|reduce|review)\b/i;

const splitSteps = (message) => {
    const lines = message.split(/\n|;|,\s*then\s+|\bthen\b/i).map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, '').trim()).filter(Boolean);
    const numbered = /(^|\n)\s*(?:[-*•]|\d+[.)])\s+\S/.test(message);
    const verbs = lines.filter((l) => STEP_VERB.test(l)).length;
    return lines.length >= 2 && (numbered || verbs >= 2) ? lines : null;
};

const sentences = (message) => message.split(/(?<=[.!?])\s+|\n+/).map((s) => s.trim()).filter((s) => s.length > 3);

const title = (s) => {
    const clean = s.replace(/[.!]+$/, '');
    return clean.length > 120 ? `${clean.slice(0, 117)}…` : clean;
};

const envelope = (category, record) => ({ ok: true, action: 'save', category, api_record: record });

// The playground has no intel, so a turn is drafted from the message alone, in the shape intel sends: the reply and the
// save envelopes it proposes. Steps go to the event's active task list when it has one, as intel does.
export const draftKnowledgeTurn = (message, event, activeList = null) => {
    const steps = splitSteps(message);
    if (steps) {
        const tasks = steps.slice(0, 8).map((s) => ({ title: title(s), description: '', actor: 'MANUAL', required: true, links: [] }));
        const name = activeList ? null : `${event.name} run`;
        return {
            reply: activeList
                ? `I've prepared ${plural(tasks.length, 'task')} for “${activeList.name}” below. Add them, or dismiss.`
                : `I've prepared a task list below: “${name}”, ${plural(tasks.length, 'task')}. Create it, or dismiss.`,
            cards: [envelope(TASKS_CARD, {
                liveEventTaskListId: activeList?.id || null, liveEventTaskListName: activeList?.name || null, newTaskListName: name, liveEventId: event.id, tasks,
            })],
        };
    }
    const all = sentences(message);
    const facts = all.filter((s) => !s.endsWith('?'));
    if (!facts.length) {
        return {
            reply: all.length
                ? "The playground can't look through this event's knowledge. Tell me a fact about how it runs, or the steps to run it."
                : 'Tell me a fact about how it runs, or the steps to run it.',
            cards: [],
        };
    }
    const cards = facts.map((s) => envelope(SNIPPET_CARD, {
        type: 'KNOWLEDGE_SNIPPET', kind: guessKind(s), title: title(s), summary: '', body: s, source: 'CHAT', system: EVENT_SYSTEM, systemId: event.id,
    }));
    const names = cards.map((c) => `“${c.api_record.title}”`).join(', ');
    return { reply: `I've prepared ${plural(cards.length, 'card')} below: ${names}. Save ${cards.length === 1 ? 'it' : 'them'}, or dismiss.`, cards };
};
